import { useContext, useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";
import styled from "styled-components";
import Block, { BlockContent, BlockGrid } from "~/components/Block";
import Card from "~/components/Card";
import { ThemeContext } from "~/lib/ThemeContext";

const CompareItem = styled.div`
  display: grid;
  grid-template-rows: auto; // Adjust based on the number of sections
  gap: 1rem;
`;

const CompareSection = styled.div`
  margin-top: 1rem;

  h3 {
    border-bottom: 1px solid ${(p) => p.theme.colors.grey};
    padding-bottom: 1rem;
    margin-bottom: 2rem;
  }
`;

const CompareSpec = styled.div`
  margin-bottom: 3rem;

  h4 {
    margin: 0.5rem 0;
  }

  p {
    margin: 0;
  }

  ul {
    margin: 0;
    padding: 0;
    height: 35px;
  }

  li {
    list-style: none;
    float: left;
  }
`;

function ProductComparisonChart(props) {
  const currentTheme = useContext(ThemeContext);
  const itemRefs = useRef([]);

  const {
    spacingTop = 0,
    spacingBottom = 6,
    backgroundColor = currentTheme.background,
    color = currentTheme.foreground,

    type = "b",
    buttonType = "outlined",
    buttonAction = "link",
    skipCrossSells = true,
    showText = true,
    showPrice = true,

    cards = [],
    columnsOnDesktop = 3,
    columnsOnMobile = 1,
  } = props;

  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  useEffect(() => {
    const sectionMaxHeights = []; // Max heights for each section
    const specMaxHeights = []; // Max heights for each CompareSpec within a section

    // Calculate max heights for sections
    itemRefs.current.forEach((item) => {
      const sections = item.querySelectorAll(".compare-section");

      sections.forEach((section, sectionIndex) => {
        // Initialize max heights for sections
        if (!sectionMaxHeights[sectionIndex]) {
          sectionMaxHeights[sectionIndex] = 0;
        }

        section.style.height = "auto"; // Reset height
        sectionMaxHeights[sectionIndex] = Math.max(
          sectionMaxHeights[sectionIndex],
          section.offsetHeight
        );

        // Calculate max heights for specs within this section
        const specs = section.querySelectorAll(".compare-spec");
        specs.forEach((spec, specIndex) => {
          if (!specMaxHeights[sectionIndex]) {
            specMaxHeights[sectionIndex] = [];
          }
          if (!specMaxHeights[sectionIndex][specIndex]) {
            specMaxHeights[sectionIndex][specIndex] = 0;
          }

          spec.style.height = "auto"; // Reset height
          specMaxHeights[sectionIndex][specIndex] = Math.max(
            specMaxHeights[sectionIndex][specIndex],
            spec.offsetHeight
          );
        });
      });
    });

    // Apply max heights for sections
    itemRefs.current.forEach((item) => {
      const sections = item.querySelectorAll(".compare-section");

      sections.forEach((section, sectionIndex) => {
        section.style.height = `${sectionMaxHeights[sectionIndex]}px`;

        // Apply max heights for specs within this section
        const specs = section.querySelectorAll(".compare-spec");
        specs.forEach((spec, specIndex) => {
          spec.style.height = `${specMaxHeights[sectionIndex][specIndex]}px`;
        });
      });
    });
  }, [props.cards]);

  return (
    <Block
      spacingTop={spacingTop}
      spacingBottom={spacingBottom}
      backgroundColor={backgroundColor}
      color={color}
    >
      <BlockContent ref={ref}>
        <BlockGrid columnsOnMobile={columnsOnMobile} cols={columnsOnDesktop}>
          {cards.map((card, i) => (
            <Card
              key={i}
              color={color}
              showProductPrice={showPrice}
              type={type}
              buttonType={buttonType}
              buttonAction={buttonAction}
              allClickable
              buttonSpacing={4}
              skipCrossSells={skipCrossSells}
              showText={showText}
              soundProfile={card.sound_profile.data || null}
              {...card}
            />
          ))}
        </BlockGrid>
        <BlockGrid columnsOnMobile={columnsOnMobile} cols={columnsOnDesktop}>
          {cards.map((card, i) => (
            <CompareItem key={i} ref={(el) => (itemRefs.current[i] = el)}>
              <CompareSection className="compare-section">
                <h3>Summary</h3>
                <p>{card.summary}</p>
              </CompareSection>

              <CompareSection>
                <h3>Connectivity</h3>

                <CompareSpec className="compare-spec">
                  <img
                    src="/static/assets/icons/connectivity-cable.svg"
                    alt="Cable icon"
                    style={{ width: "28px", height: "28px" }}
                  />
                  <h4>Cable</h4>
                  <p>{card.cable ? card.cable.name : "-"}</p>
                </CompareSpec>

                <CompareSpec className="compare-spec">
                  <ul>
                    {card.w_link && (
                      <li>
                        <img
                          src="/static/assets/icons/connectivity-w-link.svg"
                          alt="W+ Link icon"
                          style={{
                            width: "30px",
                            height: "30px",
                            marginRight: "5px",
                            float: "left",
                          }}
                        />
                      </li>
                    )}
                    {card.bluetooth && (
                      <li>
                        <img
                          src="/static/assets/icons/connectivity-bluetooth.svg"
                          alt="Bluetooth icon"
                          style={{ width: "30px", height: "30px", float: "left" }}
                        />
                      </li>
                    )}
                  </ul>
                  <h4>Wireless</h4>
                  <p>{card.w_link ? card.w_link : "-"}</p>
                  <p>{card.bluetooth ? card.bluetooth : "-"}</p>
                </CompareSpec>

                <CompareSpec className="compare-spec">
                  <img
                    src="/static/assets/icons/connectivity-battery.svg"
                    alt="Battery icon"
                    style={{ width: "35px", height: "35px" }}
                  />
                  <h4>Battery</h4>
                  <p>{card.battery ? card.battery : "-"}</p>
                </CompareSpec>

                <CompareSpec className="compare-spec">
                  {/* <img src="/static/assets/icons/connectivity-transmitter.svg" alt="Transmitter icon" style={{ width: '35px', height: '35px' }} /> */}
                  <h4>Compatibility</h4>
                  <p>{card.w_link_compatibility ? card.w_link_compatibility : "-"}</p>
                </CompareSpec>
              </CompareSection>

              <CompareSection className="compare-section">
                <h3>Audio</h3>

                <CompareSpec className="compare-spec">
                  <img
                    src="/static/assets/icons/audio-speaker-units.png"
                    alt="Speaker units icon"
                    style={{ width: "35px", height: "35px" }}
                  />
                  <h4>Speaker units</h4>
                  <p>{card.speaker_units ? card.speaker_units.name : "-"}</p>
                </CompareSpec>

                <CompareSpec className="compare-spec">
                  <img
                    src="/static/assets/icons/audio-diapraghm.svg"
                    alt="Diaphragm icon"
                    style={{ width: "35px", height: "35px" }}
                  />
                  <h4>Diaphragm</h4>
                  <p>{card.diaphragm}</p>
                </CompareSpec>

                <CompareSpec className="compare-spec">
                  <img
                    src="/static/assets/icons/audio-power.svg"
                    alt="Power icon"
                    style={{ width: "35px", height: "35px" }}
                  />
                  <h4>Power</h4>
                  <p>{card.power}</p>
                </CompareSpec>

                <CompareSpec className="compare-spec">
                  <img
                    src="/static/assets/icons/audio-spl.svg"
                    alt="SPL icon"
                    style={{ width: "35px", height: "35px" }}
                  />
                  <h4>SPL</h4>
                  <p>{card.spl}</p>
                </CompareSpec>

                <CompareSpec className="compare-spec">
                  <img
                    src="/static/assets/icons/audio-mic.svg"
                    alt="Microphone icon"
                    style={{ width: "35px", height: "35px" }}
                  />
                  <h4>Microphone</h4>
                  <p>{card.microphone ? card.microphone : "-"}</p>
                </CompareSpec>
              </CompareSection>

              {/* Built */}
              <CompareSection className="compare-section">
                <h3>Comfort</h3>

                <CompareSpec className="compare-spec">
                  {/* <img src="/static/assets/icons/comfort-headband.svg" alt="Headband icon" style={{ width: '35px', height: '35px' }} /> */}
                  <h4>Headband</h4>
                  <p>{card.headband ? card.headband.name : "-"}</p>
                </CompareSpec>

                <CompareSpec className="compare-spec">
                  {/* <img src="/static/assets/icons/comfort-headband.svg" alt="Headband icon" style={{ width: '35px', height: '35px' }} /> */}
                  <h4>Earpads</h4>
                  <p>{card.earpads ? card.earpads.name : "-"}</p>
                </CompareSpec>

                <CompareSpec className="compare-spec">
                  {/* <img src="/static/assets/icons/comfort-headband.svg" alt="Headband icon" style={{ width: '35px', height: '35px' }} /> */}
                  <h4>Weight</h4>
                  <p>{card.weight ? card.weight : "-"}</p>
                </CompareSpec>
              </CompareSection>
            </CompareItem>
          ))}
        </BlockGrid>
      </BlockContent>
    </Block>
  );
}

export default ProductComparisonChart;
